import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { ShoppingBag, Eye, ChevronLeft, ChevronRight, Package, Phone, Mail, Truck, Filter } from 'lucide-react';
import { Button, Card, Dialog, DialogContent, DialogHeader, DialogTitle, Input, Label } from '@caruso/ui';
import { ordersService, Order } from '@/services/orders.service';
import { toast } from 'sonner';

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-500/20 text-yellow-400',
  confirmed: 'bg-blue-500/20 text-blue-400',
  processing: 'bg-purple-500/20 text-purple-400',
  shipped: 'bg-cyan-500/20 text-cyan-400',
  delivered: 'bg-green-500/20 text-green-400',
  cancelled: 'bg-red-500/20 text-red-400',
};

const statusLabels: Record<string, string> = {
  pending: 'Pendente',
  confirmed: 'Confirmado',
  processing: 'Processando',
  shipped: 'Enviado',
  delivered: 'Entregue',
  cancelled: 'Cancelado',
};

const paymentStatusColors: Record<string, string> = {
  pending: 'bg-yellow-500/20 text-yellow-400',
  paid: 'bg-green-500/20 text-green-400',
  failed: 'bg-red-500/20 text-red-400',
  refunded: 'bg-gray-500/20 text-gray-400',
};

const paymentStatusLabels: Record<string, string> = {
  pending: 'Pendente',
  paid: 'Pago',
  failed: 'Falhou',
  refunded: 'Reembolsado',
};

export function OrdersPage() {
  const [page, setPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isShipDialogOpen, setIsShipDialogOpen] = useState(false);
  const [orderToShip, setOrderToShip] = useState<Order | null>(null);
  const [trackingCode, setTrackingCode] = useState('');
  const [carrier, setCarrier] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('');

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['orders', page, statusFilter, paymentStatusFilter],
    queryFn: () => ordersService.getAll({
      page,
      limit: 10,
      status: statusFilter || undefined,
      paymentStatus: paymentStatusFilter || undefined,
    }),
  });

  const shipMutation = useMutation({
    mutationFn: ({ id, trackingCode, carrier }: { id: string; trackingCode: string; carrier: string }) =>
      ordersService.markAsShipped(id, trackingCode, carrier),
    onSuccess: () => {
      toast.success('Pedido marcado como enviado!');
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setIsShipDialogOpen(false);
      setOrderToShip(null);
      setTrackingCode('');
      setCarrier('');
    },
    onError: () => {
      toast.error('Erro ao marcar pedido como enviado');
    },
  });

  const handleOpenShipDialog = (order: Order) => {
    setOrderToShip(order);
    setTrackingCode('');
    setCarrier('');
    setIsShipDialogOpen(true);
  };

  const handleShipOrder = () => {
    if (!orderToShip || !trackingCode.trim() || !carrier.trim()) {
      toast.error('Preencha todos os campos');
      return;
    }
    shipMutation.mutate({ id: orderToShip.id, trackingCode, carrier });
  };

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setIsDialogOpen(true);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(price);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPhone = (phone?: string) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');

    // Brazilian numbers with country code
    if (digits.startsWith('55') && digits.length >= 12) {
      const ddd = digits.slice(2, 4);
      const rest = digits.slice(4);
      if (rest.length === 9) {
        return `+55 (${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
      } else if (rest.length === 8) {
        return `+55 (${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
      }
    }

    // Brazilian without country code
    if (digits.length === 11) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    return phone;
  };

  const getWhatsAppUrl = (phone?: string) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    // Ensure Brazilian numbers have country code
    if (digits.length === 11 || digits.length === 10) {
      return `https://wa.me/55${digits}`;
    }
    return `https://wa.me/${digits}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif text-brand-gold">Pedidos</h1>
          <p className="text-brand-ivory/60 mt-1 text-sm sm:text-base">
            Gerencie todos os pedidos da loja
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-brand-ink/30 rounded-lg border border-brand-gold/10">
        <div className="flex items-center gap-2 text-brand-ivory/60">
          <Filter className="w-4 h-4" />
          <span className="text-sm">Filtros:</span>
        </div>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="bg-brand-ink border border-brand-gold/30 text-brand-ivory text-sm rounded-lg px-3 py-2 focus:border-brand-gold outline-none"
        >
          <option value="">Todos os status</option>
          <option value="pending">Pendente</option>
          <option value="confirmed">Confirmado</option>
          <option value="shipped">Enviado</option>
          <option value="delivered">Entregue</option>
          <option value="cancelled">Cancelado</option>
        </select>

        <select
          value={paymentStatusFilter}
          onChange={(e) => {
            setPaymentStatusFilter(e.target.value);
            setPage(1);
          }}
          className="bg-brand-ink border border-brand-gold/30 text-brand-ivory text-sm rounded-lg px-3 py-2 focus:border-brand-gold outline-none"
        >
          <option value="">Todos os pagamentos</option>
          <option value="pending">Pagamento Pendente</option>
          <option value="paid">Pago</option>
          <option value="failed">Falhou</option>
          <option value="refunded">Reembolsado</option>
        </select>

        {(statusFilter || paymentStatusFilter) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setStatusFilter('');
              setPaymentStatusFilter('');
              setPage(1);
            }}
            className="text-brand-ivory/60 hover:text-brand-gold"
          >
            Limpar filtros
          </Button>
        )}
      </div>

      {/* Orders List */}
      {isLoading ? (
        <div className="text-center py-12 text-brand-ivory/50">
          Carregando...
        </div>
      ) : data?.data.length === 0 ? (
        <div className="text-center py-12">
          <ShoppingBag className="w-16 h-16 text-brand-gold/40 mx-auto mb-4" />
          <p className="text-brand-ivory/50 mb-4">
            Nenhum pedido encontrado
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4">
            {data?.data.map((order) => (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Card className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all p-4 sm:p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
                    {/* Order Info */}
                    <div className="flex-1 space-y-2">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <h3 className="font-serif text-brand-gold text-base sm:text-lg">
                          Pedido #{order.orderNumber}
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          <span className={`text-xs px-2 py-1 rounded ${statusColors[order.status]}`}>
                            {statusLabels[order.status]}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${paymentStatusColors[order.paymentStatus]}`}>
                            {paymentStatusLabels[order.paymentStatus]}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        <div className="flex items-center gap-2 text-brand-ivory/60">
                          <span className="font-semibold text-brand-ivory">Cliente:</span>
                          <span className="text-brand-ivory hover:text-brand-gold cursor-pointer transition-colors">
                            {order.customerName}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-brand-ivory/60">
                          <Mail className="w-4 h-4" />
                          {order.customerEmail}
                        </div>
                        <div className="flex items-center gap-2 text-brand-ivory/60">
                          <Phone className="w-4 h-4" />
                          <a
                            href={getWhatsAppUrl(order.customerPhone)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-gold hover:underline"
                          >
                            {formatPhone(order.customerPhone)}
                          </a>
                        </div>
                        <div className="flex items-center gap-2 text-brand-ivory/60">
                          <Package className="w-4 h-4" />
                          {order.items.length} {order.items.length === 1 ? 'item' : 'itens'}
                        </div>
                      </div>

                      <p className="text-xs text-brand-ivory/50">
                        Realizado em: {formatDate(order.createdAt)}
                      </p>
                    </div>

                    {/* Price and Actions */}
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-brand-ivory/60">Total</p>
                        <p className="text-2xl font-bold text-brand-gold">
                          {formatPrice(order.total)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {order.paymentStatus === 'paid' && order.status !== 'shipped' && order.status !== 'delivered' && (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleOpenShipDialog(order)}
                            title="Marcar como enviado"
                            className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
                          >
                            <Truck className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewOrder(order)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {data && data.meta.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-4 text-brand-ivory">
                Página {page} de {data.meta.totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setPage(page + 1)}
                disabled={page === data.meta.totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Order Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-brand-ink border-brand-gold/20 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-brand-gold">
              Detalhes do Pedido #{selectedOrder?.orderNumber}
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex gap-2">
                <span className={`text-xs px-3 py-1 rounded ${statusColors[selectedOrder.status]}`}>
                  {statusLabels[selectedOrder.status]}
                </span>
                <span className={`text-xs px-3 py-1 rounded ${paymentStatusColors[selectedOrder.paymentStatus]}`}>
                  Pagamento: {paymentStatusLabels[selectedOrder.paymentStatus]}
                </span>
              </div>

              {/* Customer Info */}
              <div className="border-t border-brand-gold/10 pt-4">
                <h3 className="font-semibold text-brand-ivory mb-3">Dados do Cliente</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-brand-ivory/60">Nome:</span>
                    <p className="text-brand-ivory hover:text-brand-gold cursor-pointer transition-colors">
                      {selectedOrder.customerName}
                    </p>
                  </div>
                  <div>
                    <span className="text-brand-ivory/60">Email:</span>
                    <p className="text-brand-ivory">{selectedOrder.customerEmail}</p>
                  </div>
                  <div>
                    <span className="text-brand-ivory/60">Telefone:</span>
                    <a
                      href={getWhatsAppUrl(selectedOrder.customerPhone)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-gold hover:underline block"
                    >
                      {formatPhone(selectedOrder.customerPhone)}
                    </a>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="border-t border-brand-gold/10 pt-4">
                <h3 className="font-semibold text-brand-ivory mb-3">Endereço de Entrega</h3>
                <p className="text-sm text-brand-ivory/80">
                  {selectedOrder.address}, {selectedOrder.number}
                  {selectedOrder.complement && ` - ${selectedOrder.complement}`}
                </p>
                <p className="text-sm text-brand-ivory/80">
                  {selectedOrder.neighborhood} - {selectedOrder.city}/{selectedOrder.state}
                </p>
                <p className="text-sm text-brand-ivory/80">
                  CEP: {selectedOrder.zipCode}
                </p>
              </div>

              {/* Items */}
              <div className="border-t border-brand-gold/10 pt-4">
                <h3 className="font-semibold text-brand-ivory mb-3">Itens do Pedido</h3>
                <div className="space-y-3">
                  {selectedOrder.items.map((item) => (
                    <div key={item.id} className="flex items-center justify-between gap-4 p-3 bg-brand-smoke/30 rounded-lg">
                      <div className="flex-1">
                        <p className="text-brand-ivory font-medium">{item.productName}</p>
                        <p className="text-sm text-brand-ivory/60">
                          {item.quantity}x {formatPrice(item.price)}
                        </p>
                      </div>
                      <p className="text-brand-gold font-semibold">
                        {formatPrice(item.subtotal)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-brand-gold/10 pt-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-brand-ivory/60">Subtotal:</span>
                    <span className="text-brand-ivory">{formatPrice(selectedOrder.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-brand-ivory/60">Frete:</span>
                    <span className="text-brand-ivory">{formatPrice(selectedOrder.shipping)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t border-brand-gold/10 pt-2">
                    <span className="text-brand-gold">Total:</span>
                    <span className="text-brand-gold">{formatPrice(selectedOrder.total)}</span>
                  </div>
                </div>
              </div>

              {/* Shipping Info */}
              {selectedOrder.trackingCode && (
                <div className="border-t border-brand-gold/10 pt-4">
                  <h3 className="font-semibold text-brand-ivory mb-3 flex items-center gap-2">
                    <Truck className="w-4 h-4 text-cyan-400" />
                    Informações de Envio
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm bg-cyan-500/10 p-3 rounded-lg border border-cyan-500/20">
                    <div>
                      <span className="text-brand-ivory/60">Código de Rastreio:</span>
                      <p className="text-cyan-400 font-mono">{selectedOrder.trackingCode}</p>
                    </div>
                    <div>
                      <span className="text-brand-ivory/60">Transportadora:</span>
                      <p className="text-brand-ivory">{selectedOrder.carrier}</p>
                    </div>
                    {selectedOrder.shippedAt && (
                      <div className="col-span-full">
                        <span className="text-brand-ivory/60">Enviado em:</span>
                        <p className="text-brand-ivory">{formatDate(selectedOrder.shippedAt)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedOrder.notes && (
                <div className="border-t border-brand-gold/10 pt-4">
                  <h3 className="font-semibold text-brand-ivory mb-2">Observações</h3>
                  <p className="text-sm text-brand-ivory/80">{selectedOrder.notes}</p>
                </div>
              )}

              <div className="text-xs text-brand-ivory/50 border-t border-brand-gold/10 pt-4">
                Pedido realizado em: {formatDate(selectedOrder.createdAt)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Ship Order Dialog */}
      <Dialog open={isShipDialogOpen} onOpenChange={setIsShipDialogOpen}>
        <DialogContent className="bg-brand-ink border-brand-gold/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-brand-gold flex items-center gap-2">
              <Truck className="w-5 h-5" />
              Marcar Pedido como Enviado
            </DialogTitle>
          </DialogHeader>

          {orderToShip && (
            <div className="space-y-4">
              <p className="text-brand-ivory/80 text-sm">
                Pedido <span className="text-brand-gold font-semibold">#{orderToShip.orderNumber}</span> - {orderToShip.customerName}
              </p>

              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="trackingCode" className="text-brand-ivory">
                    Código de Rastreio
                  </Label>
                  <Input
                    id="trackingCode"
                    value={trackingCode}
                    onChange={(e) => setTrackingCode(e.target.value)}
                    placeholder="Ex: BR123456789BR"
                    className="bg-brand-smoke/50 border-brand-gold/30 text-brand-ivory placeholder:text-brand-ivory/40"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="carrier" className="text-brand-ivory">
                    Transportadora
                  </Label>
                  <Input
                    id="carrier"
                    value={carrier}
                    onChange={(e) => setCarrier(e.target.value)}
                    placeholder="Ex: Correios, Jadlog, Sequoia..."
                    className="bg-brand-smoke/50 border-brand-gold/30 text-brand-ivory placeholder:text-brand-ivory/40"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setIsShipDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                  onClick={handleShipOrder}
                  disabled={shipMutation.isPending}
                >
                  {shipMutation.isPending ? 'Enviando...' : 'Confirmar Envio'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
